import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import FlatLayout from 'components/Layout/FlatLayout';
import PageDescription from 'components/PageDescription';
import FaqJsonLd from 'components/Seo/FaqJsonLd';

import { getStoreCouponsMeta } from 'lib/couponPage';
import getDefaultFaq from 'lib/faq/getDefaultFaq';
import parseFaqFromHtml from 'lib/faq/parseFaqFromHtml';
import { trackEvent } from 'lib/posthog';
import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import Breadcrumb from './Breadcrumb';
import FaqSection from './FaqSection';
import Head from './Head';
import Schema from './Schema';
import Sidebar from './Sidebar';
import StoreSummary from './StoreSummary';
import TimelineList from './TimelineList';

import COUPONS from 'constants/coupons';

const StoreAd = dynamic(() => import('./StoreAd'));

/**
 * We used to a specific field for it that came from the API. These titles
 * were being repeated for both offers and coupons pages from the same store.
 * We replaced it with the fn below so it doesn't causes SEO issues.
 */
const toMetaTitle = ({ isOfferPage, storeName }) =>
  isOfferPage
    ? `As melhores ofertas da ${storeName} - É no Promobit`
    : `Cupons de desconto ${storeName} - É no Promobit`;

const Store = ({
  gaEventCategory = 'offers_store',
  isExclusivelyCoupon = false,
  isStoreChecklistActive = true,
  serverCoupons,
  serverDate,
  serverDateFormatted,
  serverMonth,
  serverYear,
  serverOffers,
  store: {
    couponPageDescription,
    googleIndex,
    hasPhysicalStore,
    isStoreCnpjValid,
    isStoreVerified,
    offerPageDescription,
    offersMetaDescription,
    storeAddress,
    storeContacts,
    storeDescription,
    storeDomain,
    storeDomainEnriched,
    storeFacebookPage,
    storeId,
    storeImage,
    storeName,
    storeRating,
    storeRatingCount,
    storeSlug,
  },
  storeAd,
  fixedOffer = null,
}) => {
  const { isUtmLoaded } = useUtm();

  const isOfferPage = !isExclusivelyCoupon && !!serverOffers;

  const activeCouponCount = useMemo(
    () =>
      !isOfferPage
        ? serverCoupons?.coupons?.filter(
            // API only returns APPROVED and FINISHED for this endpoint;
            // !FINISHED is equivalent to APPROVED in practice.
            (c) => c.couponStatusName !== COUPONS.STATUS.FINISHED
          )?.length
        : 0,
    [isOfferPage, serverCoupons?.coupons]
  );

  useEffect(() => {
    if (isUtmLoaded) {
      const endpoint = isOfferPage ? '/st' : '/cp';

      sendSupixEvent(endpoint, {
        id: storeId,
      });
    }
  }, [storeId, isOfferPage, isUtmLoaded]);

  useEffect(() => {
    trackEvent(
      isOfferPage ? 'view store offers page' : 'view coupon store page',
      {
        type: 'screen',
        source_type: 'navigation',
      }
    );
  }, [storeSlug, isOfferPage]);

  const isStoreAdVisible = !!storeAd?.image;
  const { title: storeCouponTitle, description: storeCouponDescription } =
    getStoreCouponsMeta({
      activeCoupons: serverCoupons?.coupons || [],
      storeName,
    });

  const dynamicMetaTitle = isOfferPage
    ? toMetaTitle({ isOfferPage, storeName })
    : storeCouponTitle;

  const dynamicMetaDescription = isOfferPage
    ? offersMetaDescription
    : storeCouponDescription;

  // Graceful degradation: try parsing custom FAQ (h3+p pairs) from the
  // store's admin HTML. Falls back to generic FAQ with storeName until
  // Marketing updates each store's content to the expected format.
  const faqItems = useMemo(() => {
    if (isOfferPage) return [];

    const parsed = parseFaqFromHtml(couponPageDescription);

    return parsed.length > 0 ? parsed : getDefaultFaq(storeName);
  }, [isOfferPage, couponPageDescription, storeName]);

  // Strip FAQ h3+p pairs from the page description to avoid rendering
  // the same content twice (once in the FaqSection accordion, once in
  // PageDescription below it).
  const pageDescription = useMemo(() => {
    const raw = isOfferPage ? offerPageDescription : couponPageDescription;

    if (!raw || isOfferPage || typeof raw !== 'string') return raw;

    return (
      raw.replace(/<h3[^>]*>.*?<\/h3>\s*<p[^>]*>.*?<\/p>/gis, '').trim() || null
    );
  }, [isOfferPage, offerPageDescription, couponPageDescription]);

  return (
    <>
      <FlatLayout.Margin className="gap-8 lg:gap-12">
        <FlatLayout.Heading>
          <Head
            googleIndex={googleIndex}
            metaDescription={dynamicMetaDescription}
            metaTitle={dynamicMetaTitle}
            // ≤5 active coupons = "thin" page per RF-12; threshold avoids
            // indexing near-empty store pages that hurt crawl quality.
            // undefined guard prevents false noindex on transient SSR errors.
            noindex={
              !isOfferPage &&
              activeCouponCount !== undefined &&
              activeCouponCount <= 5
            }
          />
          <Schema
            isOfferPage={isOfferPage}
            serverDate={serverDate}
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
            storeRating={storeRating}
            storeRatingCount={storeRatingCount}
            storeSlug={storeSlug}
          />
          <Breadcrumb isOfferPage={isOfferPage} storeName={storeName} />
          <StoreSummary
            isExclusivelyCoupon={isExclusivelyCoupon}
            isStoreVerified={isStoreVerified}
            serverDateFormatted={serverDateFormatted}
            serverMonth={serverMonth}
            serverYear={serverYear}
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
          />
        </FlatLayout.Heading>
        <FlatLayout.Body>
          {isStoreAdVisible && (
            <ErrorBoundary>
              <StoreAd
                className="mb-6"
                gaEventCategory={gaEventCategory}
                storeName={storeName}
                {...storeAd}
              />
            </ErrorBoundary>
          )}
          <TimelineList
            fixedOffer={fixedOffer}
            gaEventCategory={gaEventCategory}
            isOfferPage={isOfferPage}
            serverCoupons={serverCoupons}
            serverOffers={serverOffers}
            storeId={storeId}
            storeName={storeName}
            storeSlug={storeSlug}
          />
          {faqItems.length > 0 && (
            <>
              <FaqJsonLd faqItems={faqItems} />
              <ErrorBoundary>
                <FaqSection
                  className="mt-12 lg:mt-16"
                  faqItems={faqItems}
                  storeName={storeName}
                />
              </ErrorBoundary>
            </>
          )}
          {pageDescription && faqItems.length === 0 && (
            <ErrorBoundary>
              <PageDescription
                className="mt-18"
                pageDescription={pageDescription}
              />
            </ErrorBoundary>
          )}
        </FlatLayout.Body>
        <Sidebar
          gaEventCategory={gaEventCategory}
          hasPhysicalStore={hasPhysicalStore}
          isCnpjValid={isStoreCnpjValid}
          isOfferPage={isOfferPage}
          isStoreChecklistActive={isStoreChecklistActive}
          isVerified={isStoreVerified}
          storeAddress={storeAddress}
          storeContacts={storeContacts}
          storeDescription={storeDescription}
          storeDomain={storeDomain}
          storeDomainEnriched={storeDomainEnriched}
          storeFacebookPage={storeFacebookPage}
          storeName={storeName}
          storeRating={storeRating}
          storeRatingCount={storeRatingCount}
          storeSlug={storeSlug}
        />
        <FlatLayout.ScrollToTop gaEventCategory={gaEventCategory} />
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

Store.propTypes = {
  fixedOffer: PropTypes.object,
  gaEventCategory: PropTypes.string,
  isExclusivelyCoupon: PropTypes.bool,
  isStoreChecklistActive: PropTypes.bool,
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }),
  serverOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  store: PropTypes.shape({
    storeId: PropTypes.number.isRequired,
    storeName: PropTypes.string.isRequired,
    couponPageDescription: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
    ]),
    offerPageDescription: PropTypes.string,
    storeDescription: PropTypes.string,
    pageCouponsTitle: PropTypes.string,
    pageOffersTitle: PropTypes.string,
    offersMetaDescription: PropTypes.string,
    storeImage: PropTypes.string,
    storeSlug: PropTypes.string.isRequired,
    storeDomain: PropTypes.string.isRequired,
    storeDomainEnriched: PropTypes.string,
    storeRating: PropTypes.number,
    storeRatingCount: PropTypes.number,
    storeContacts: PropTypes.string,
    storeAddress: PropTypes.string,
    storeFacebookPage: PropTypes.string,
    googleIndex: PropTypes.bool,
    isStoreIndexed: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
  }).isRequired,
  storeAd: PropTypes.shape({
    description: PropTypes.string.isRequired,
    gaEventCategory: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    storeName: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
};

export default Store;
